<script setup>
import { ref, onMounted, computed } from "vue";
import { EyeIcon, EyeOffIcon } from "@heroicons/vue/outline";
import InputLabel from "@units/InputLabel/InputLabel.vue";
import LoginButton from "@units/Button/LoginButton.vue";
import LoadingSpinner from "@units/LoadingSpinner/LoadingSpinner.vue";
import { brandUrl as brandLogos } from "@constants/brands";
import axios from "axios";

const props = defineProps({
    submiturl: {
        type: String,
        default: "",
    },
    usecsrftoken: {
        type: Boolean,
        default: false,
    },
    errors: {
        type: String,
        default: "",
    },
    token: {
        type: String,
        default: "",
    },
    eventTrackingOrigin: {
        type: String,
        default: "",
    },
    email: {
        type: String,
        default: "",
    },
    formType: {
        type: String,
        default: "reset",
    },
    isMobile: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(["submit-success", "submit-error"]);
const passwordInput = ref("");
const confirmPasswordInput = ref("");
const passwordErrors = ref("");
const confirmPasswordErrors = ref("");
const isLoading = ref(false);
const isPasswordVisible = ref(false);

const handleConfirmPasswordChange = (val) => {
    if (val.length < 8 && val.length > 0) {
        confirmPasswordErrors.value =
            "The password must be at least 8 characters";
    } else if (val !== passwordInput.value) {
        confirmPasswordErrors.value = "Must match the new password";
    } else {
        confirmPasswordErrors.value = "";
    }
    confirmPasswordInput.value = val;
};

const handlePasswordChange = (val) => {
    if (val.length < 8 && val.length > 0) {
        passwordErrors.value = "The password must be at least 8 characters";
    } else {
        passwordErrors.value = "";
    }
    passwordInput.value = val;
};

const buildFormData = () => {
    const formData = {
        password: passwordInput.value,
        password_confirmation: confirmPasswordInput.value,
        email: props.email,
    };

    // Add token based on form type
    if (props.formType === "create") {
        formData.verification_token = props.token;
        formData.event_tracking_origin = props.eventTrackingOrigin;
    } else {
        formData.token = props.token;
    }

    // Add CSRF token if needed
    if (props.usecsrftoken) {
        const csrfToken = document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute("content");
        if (csrfToken) {
            formData._token = csrfToken;
        }
    }

    return formData;
};

const handleMobileResponse = (data) => {   // Create deep link to open native Musora app
    const deepLink = `musora://login?email=${encodeURIComponent(
        props.email
    )}&token=${encodeURIComponent(data.token)}`;
    window.location.href = deepLink;
};

const handleWebResponse = (responseData) => {
    if (responseData.redirect_url) {
        window.location.href = responseData.redirect_url;
        return;
    }

    if (responseData.redirect) {
        window.location.href = responseData.redirect;
        return;
    }

    // Default redirect using current base URL
    const baseUrl = `${window.location.protocol}//${window.location.host}`;
    window.location.href = `${baseUrl}/drumeo`;
};

const handleValidationErrors = (error) => {
    if (!error.response?.data?.errors) return;

    const errors = error.response.data.errors;

    if (errors.password) {
        passwordErrors.value = errors.password[0];
    }

    if (errors.password_confirmation) {
        confirmPasswordErrors.value = errors.password_confirmation[0];
    }
};

const handleButtonClick = async (e) => {
    isLoading.value = true;

    try {
        const formData = buildFormData();
        const response = await axios.post(
            props.submiturl || "/user-management-system/create-account-submit",
            formData
        );

        emit("submit-success", response.data);

        if (props.isMobile) {
            handleMobileResponse(response.data);
            return;
        }

        handleWebResponse(response.data);
    } catch (error) {
        emit("submit-error", error.response?.data || error.message);
        handleValidationErrors(error);
    } finally {
        isLoading.value = false;
    }
};

const toggleSeePassword = () => {
    isPasswordVisible.value = !isPasswordVisible.value;
};

const isButtonDisabled = computed(() => {
    return (
        passwordInput.value.length === 0 ||
        confirmPasswordInput.value.length === 0 ||
        passwordInput.value !== confirmPasswordInput.value ||
        isLoading.value
    );
});

const constantTexts = {
    reset: {
        formTitle: "Create your new password",
        descriptionFirst: "Create a new password for",
        descriptionLast: "",
        ctaText: "RESET PASSWORD",
        ctaLoadingText: "RESETTING PASSWORD...",
    },
    create: {
        formTitle: "Create your password",
        descriptionFirst: "Create a password for",
        descriptionLast: "You'll use this password to log in to your account.",
        ctaText: "CREATE YOUR ACCOUNT",
        ctaLoadingText: "CREATING ACCOUNT...",
    },
};

onMounted(() => {
    document.getElementById("newPassword").focus();
});
</script>

<template>
    <div class="tw-z-0 tw-h-[100vh] tw-w-full tw-bg-[#000C17]">
        <div
            class="tw-absolute tw-z-20 tw-flex tw-min-h-screen tw-w-full tw-flex-col tw-items-center tw-justify-center tw-px-[20px] tw-text-white"
        >
            <section
                id="logoContainer"
                class="tw-flex tw-w-full tw-max-w-[400px] tw-flex-col tw-items-center tw-border-b-[1px] tw-border-[#223F57] tw-pb-[40px] tw-text-center"
            >
                <img
                    class="tw-mb-[15px] tw-w-full tw-max-w-[130px] md:tw-max-w-[240px]"
                    src="https://d38h3dn806jqj1.cloudfront.net/logos/musora-white_new.svg"
                    alt="Musora Logo"
                />
                <p class="tw-text-[14px] tw-leading-[21px] tw-text-[#9EC0DC]">
                    The ultimate music lessons experience
                </p>
                <p
                    class="tw-mt-[4px] tw-flex tw-h-[18px] tw-w-full tw-justify-center"
                >
                    <img
                        class="tw-mr-[9px] tw-h-[15px] tw-self-start"
                        :src="brandLogos.drumeo"
                        alt="Drumeo Logo"
                    />
                    <img
                        class="tw-mr-[9px] tw-h-[15px] tw-self-center"
                        :src="brandLogos.pianote"
                        alt="Pianote Logo"
                    />
                    <img
                        class="tw-mr-[9px] tw-h-[16px] tw-self-end"
                        :src="brandLogos.guitareo"
                        alt="Guitareo Logo"
                    />
                    <img
                        class="tw-h-[16px] tw-self-end"
                        :src="brandLogos.singeo"
                        alt="Singeo Logo"
                    />
                </p>
            </section>

            <section
                class="tw-flex tw-w-full tw-max-w-[400px] tw-flex-col tw-items-center"
            >
                <form
                    method="post"
                    @submit.prevent
                    :action="submiturl"
                    class="tw-flex tw-w-full tw-flex-col tw-py-[40px]"
                >
                    <slot v-if="usecsrftoken" name="csrf"></slot>
                    <input
                        type="hidden"
                        :name="
                            formType === 'create'
                                ? 'verification_token'
                                : 'token'
                        "
                        :value="token"
                    />
                    <input
                        v-if="formType === 'create'"
                        type="hidden"
                        name="event_tracking_origin"
                        :value="eventTrackingOrigin"
                    />
                    <input type="hidden" name="email" :value="email" />
                    <div class="tw-mb-[20px] tw-flex tw-flex-col">
                        <h2
                            class="tw-w-full tw-pb-[10px] tw-text-center tw-text-[24px] tw-font-bold tw-leading-[36px]"
                        >
                            {{ constantTexts[formType].formTitle }}
                        </h2>
                        <p
                            class="tw-mb-[30px] tw-px-[13px] tw-text-center tw-text-[16px] tw-leading-[24px]"
                        >
                            {{ constantTexts[formType].descriptionFirst }}
                            <strong>{{ email }}.</strong>
                            {{ constantTexts[formType].descriptionLast }}
                        </p>
                        <InputLabel
                            :inputErrors="passwordErrors"
                            infoMessage="Minimum of 8 characters"
                            inputOverride="tw-w-full tw-h-[50px] tw-text-[#00101D]"
                            :inputType="isPasswordVisible ? 'text' : 'password'"
                            id="newPassword"
                            inputName="password"
                            labelValue="Password"
                            placeholder="Enter your password..."
                            @onChange="handlePasswordChange"
                            :showCustomButton="true"
                        >
                            <template #custom-btn>
                                <button
                                    type="button"
                                    class="tw-absolute tw-right-3 tw-flex tw-h-[24px] tw-w-[24px] tw-items-center tw-justify-center tw-text-black"
                                    @click="toggleSeePassword"
                                >
                                    <EyeIcon
                                        class="tw-h-[24px] tw-w-[24px]"
                                        v-if="!isPasswordVisible"
                                    />
                                    <EyeOffIcon
                                        class="tw-h-[24px] tw-w-[24px]"
                                        v-if="isPasswordVisible"
                                    />
                                </button>
                            </template>
                        </InputLabel>
                    </div>
                    <div class="tw-relative tw-mb-[20px] tw-flex tw-flex-col">
                        <InputLabel
                            :inputErrors="confirmPasswordErrors"
                            infoMessage="Must match the new password"
                            wrapperOverride="tw-text-[16px]"
                            inputOverride="tw-w-full tw-h-[50px] tw-text-[#00101D]"
                            :inputType="isPasswordVisible ? 'text' : 'password'"
                            id="confirmNewPassword"
                            inputName="password_confirmation"
                            labelValue="Confirm password"
                            placeholder="Confirm your password..."
                            @onChange="handleConfirmPasswordChange"
                            @onEnter="handleButtonClick"
                            :showCustomButton="true"
                        >
                            <template #custom-btn>
                                <button
                                    type="button"
                                    class="tw-absolute tw-right-3 tw-flex tw-h-[24px] tw-w-[24px] tw-items-center tw-justify-center tw-text-black"
                                    @click="toggleSeePassword"
                                >
                                    <EyeIcon
                                        class="tw-h-[24px] tw-w-[24px]"
                                        v-if="!isPasswordVisible"
                                    />
                                    <EyeOffIcon
                                        class="tw-h-[24px] tw-w-[24px]"
                                        v-if="isPasswordVisible"
                                    />
                                </button>
                            </template>
                        </InputLabel>
                    </div>

                    <LoginButton
                        :disabled="isButtonDisabled"
                        type="button"
                        @on-button-click="handleButtonClick"
                    >
                        <span
                            class="tw-flex tw-items-center tw-justify-center"
                            v-if="isLoading"
                        >
                            <LoadingSpinner />
                            {{ constantTexts[formType].ctaLoadingText }}
                        </span>
                        <span v-if="!isLoading">{{
                            constantTexts[formType].ctaText
                        }}</span>
                    </LoginButton>
                </form>
            </section>
        </div>
    </div>
</template>

<style type="text/css">
.password-label,
.password_confirmation-label {
    font-weight: 700;
    font-size: 16px;
}
</style>
